<template>
  <div
    :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]"
    v-test="'insights-heatmap'"
  >
    <Flex :gap=".25">
      <div :class="[$style.column, $style.columnY]">
        <div
          v-for="(item, index) in y"
          :key="`y_${index}`"
          :class="$style.cell"
          v-test="'insights-heatmap-y'"
        >
          <div :class="$style.cellContent">
            <BaseIcon
              v-if="index === 0"
              name="sun"
              size="s"
            />
            <BaseIcon
              v-else-if="index === y.length - 1"
              name="moon"
              size="s"
            />
            {{ formatTime(item) }}
          </div>
        </div>
        <div :class="$style.colFooter">{{ $t('reports.totals.totals') }}</div>
      </div>
      <div
        v-for="(week, index) in days"
        :key="index"
        :class="$style.column"
      >
        <div
          :class="$style.colHeader"
          v-test="'insights-heatmap-x'"
        >
          {{ x[index] }}
        </div>
        <div
          v-for="(item, index) in week.items"
          :key="`item_${index}`"
          :class="[
            $style.cell,
            {
              [$style.filled]: getRatio(item.sum) > 0.6
            }
          ]"
          v-test="'insights-heatmap-sum'"
        >
          <div :class="$style.cellContent">{{ item.sum }}</div>
          <div
            :class="[
              $style.cellBg,
              {
                [$style.cellBgEmpty]: !item.sum
              }
            ]"
            :style="{ opacity: getRatio(item.sum) || 1 }"
          />
        </div>
        <div
          :class="$style.colFooter"
          v-test="'insights-heatmap-footer'"
        >
          {{ week.total }}
        </div>
      </div>
    </Flex>
  </div>
</template>

<script
  setup
  lang="ts"
>
import filters from '@/filters';
import { groupBy } from '@/helpers/formatting';
import { formatTime } from './helpers';
import type { HeatMapInsight } from './types';

const props = defineProps<{
  data: HeatMapInsight[];
}>();

const y = computed(() => {
  return ['09-12', '12-15', '15-18', '18-21'];
});

const x = Array.from({ length: 7 }, (_x, i) => filters.weekdayShort(i + 1));

const days = computed(() =>
  groupBy(props.data, 'date').map((group) => ({
    ...group,
    total: group.items.reduce(
      (acc: number, val: HeatMapInsight) => acc + val.sum,
      0,
    ),
  })),
);

const highestSum = computed(() =>
  Math.max(
    ...days.value.flatMap((obj) =>
      obj.items.map((item: HeatMapInsight) => item.sum),
    ),
  ),
);

const getRatio = (sum: number) => sum / highestSum.value;
</script>

<style
  lang="scss"
  module
>
.column {
  padding: 3px;

  &:first-child {
    width: 19.5%;

    .cellContent, .colFooter {
      justify-content: flex-end;
      padding-right: $spacing;
    }
  }

  &:not(:first-child) {
    width: 11.5%;
  }
}

$cellHeight: 40px;

.colHeader,
.colFooter {
  display: flex;
  align-items: center;
  justify-content: center;
  height: $cellHeight;

  .base.smallScreen & {
    font-size: 12px;
  }
}

.columnY {
  padding-top: $cellHeight + 3px;
  font-size: 11px;

  .base.smallScreen & {
    font-size: 10px;
  }
}

.cell {
  position: relative;
  height: $cellHeight;
  margin-bottom: 6px;

  &.filled {
    color: white;
  }
}

.cellContent,
.cellBg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.cellContent {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  gap: $spacing * .25;
}

.cellBg {
  background-color: var(--color-primary);
  border-radius: 8px;
}

.cellBgEmpty {
  background-color: var(--color-bg-secondary);
}
</style>
